/**
 * The rule behind useDismissGuard (dismissGuard.tsx, A-#195 2): a backdrop click counts only when pressed and released on
 * the backdrop; while a changed field is still on the page, every way out asks instead of closing.
 *
 * "Changed" is the fields typed into, kept while they are still there (A-#199 1). A submit clears nothing: a save the
 * server refuses (400, 403) leaves its form, and the text in it, on the page, so closing still asks. A save that succeeds
 * closes or empties its form (the cards' forms all do), its fields leave the page, and nothing is left to lose. A form
 * that stayed after a successful save would ask once more on closing: it errs toward asking, never toward losing.
 *
 * A field is known by its element AND by its name (A-#201): a form re-drawn after a refusal (a field remounted, a new
 * element showing the same typed text) is still the same field, so it still asks. `present(name)` says whether a field
 * of that name is still in the pop-up.
 */
export interface Field { isConnected: boolean; name?: string | null }

export function dismissRule(close: () => void, ask: () => void,
  { changes = true, present = () => false }: { changes?: boolean; present?: (name: string) => boolean } = {}) {
  let downOnBackdrop = false;
  const changed = new Set<Field>();
  const dirty = () => [...changed].some((f) => f.isConnected || (f.name ? present(f.name) : false));
  const request = () => { if (changes && dirty()) ask(); else close(); };
  return {
    request,
    pointerDown: (onBackdrop: boolean) => { downOnBackdrop = onBackdrop; },
    click: (onBackdrop: boolean) => {
      const backdrop = downOnBackdrop && onBackdrop;
      downOnBackdrop = false;
      if (backdrop) request();
    },
    /** A field typed into (the event's target, and its name). */
    changed: (field: Field) => { changed.add(field); },
    /** Discarded: nothing changed is kept. */
    discarded: () => { changed.clear(); },
  };
}
